/**
 * RepeatOffendersCard — entities that were high-risk **both yesterday
 * and today**. Yesterday-only or today-only entries are noise; the
 * intersection is what matters. Three small lists side by side: IPs,
 * JA4 fingerprints, ASNs.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { fmtNumber, truncateMiddle } from "@/lib/format";
import type { EntityTypeV1, RepeatOffenderRow } from "@/types/v1";

interface Props {
  onEntity?: (type: EntityTypeV1, value: string) => void;
}

export function RepeatOffendersCard({ onEntity }: Props) {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["repeat-offenders", filters, tick],
    queryFn: ({ signal }) => api.repeatOffenders(filters, signal),
  });

  const data = q.data?.data;
  const empty = !data || (data.ips.length + data.ja4s.length + data.asns.length) === 0;

  return (
    <Card>
      <CardHeader
        title="Repeat offenders"
        subtitle="Entities that were high-risk both yesterday and today (UTC). Yesterday-only or today-only entries are excluded."
      />
      {q.isLoading ? (
        <Skeleton className="h-48" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : empty ? (
        <EmptyState title="No entities have been high-risk on both days." />
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          <Column title="IPs"  rows={data!.ips}  type="ip"  onEntity={onEntity} />
          <Column title="JA4 fingerprints" rows={data!.ja4s} type="ja4" onEntity={onEntity} mono />
          <Column title="ASNs" rows={data!.asns} type="asn" onEntity={onEntity} prefix="AS" />
        </div>
      )}
    </Card>
  );
}

function Column({ title, rows, type, onEntity, mono, prefix }: {
  title: string;
  rows: RepeatOffenderRow[];
  type: EntityTypeV1;
  onEntity?: (type: EntityTypeV1, value: string) => void;
  mono?: boolean;
  prefix?: string;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium tracking-wide text-fg-subtle">{title}</div>
      {rows.length === 0 ? (
        <div className="rounded border border-border bg-surface-2 px-3 py-3 text-xs text-fg-subtle">No matches.</div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded border border-border bg-surface-2">
          {rows.map((r) => (
            <li key={r.value} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
              <MonoValue
                value={(prefix ?? "") + (mono ? truncateMiddle(r.value, 22) : r.value)}
                onClick={onEntity ? () => onEntity(type, r.value) : undefined}
              />
              <span
                className="whitespace-nowrap font-mono text-sev-high"
                title={`Yesterday ${fmtNumber(r.yesterday)} \u2022 Today ${fmtNumber(r.today)}`}
              >
                {fmtNumber(r.yesterday, { compact: true })} + {fmtNumber(r.today, { compact: true })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
